<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AiKnowledgeSourcesAPI from 'dashboard/api/aiKnowledgeSources';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'created']);

const MAX_FILE_BYTES = 10 * 1024 * 1024;

const { t } = useI18n();

const name = ref('');
const sourceType = ref('file');
const sourceUrl = ref('');
const selectedFile = ref(null);
const isSaving = ref(false);
const isDragOver = ref(false);
const fileInputRef = ref(null);

const formatBytes = bytes => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const selectedFileSizeLabel = computed(() => {
  const used = selectedFile.value?.size || 0;
  return t('AI_AGENTS.KNOWLEDGE.MODAL.FILE_SIZE', {
    used: formatBytes(used),
    max: formatBytes(MAX_FILE_BYTES),
  });
});

const canSubmit = computed(() => {
  if (!name.value.trim()) return false;
  if (sourceType.value === 'file') {
    return (
      Boolean(selectedFile.value) && selectedFile.value.size <= MAX_FILE_BYTES
    );
  }
  return Boolean(sourceUrl.value.trim());
});

const resetForm = () => {
  name.value = '';
  sourceType.value = 'file';
  sourceUrl.value = '';
  selectedFile.value = null;
  isDragOver.value = false;
};

const close = () => {
  resetForm();
  emit('close');
};

const pickFile = () => {
  fileInputRef.value?.click();
};

const setFile = file => {
  if (!file) return;
  if (file.size > MAX_FILE_BYTES) {
    useAlert(t('AI_AGENTS.KNOWLEDGE.MODAL.FILE_TOO_LARGE'));
    return;
  }
  selectedFile.value = file;
};

const onFileInputChange = event => {
  const [file] = event.target.files || [];
  setFile(file);
  event.target.value = '';
};

const onDrop = event => {
  isDragOver.value = false;
  const [file] = event.dataTransfer?.files || [];
  setFile(file);
};

const submit = async () => {
  if (!canSubmit.value || isSaving.value) return;

  isSaving.value = true;
  try {
    if (sourceType.value === 'file') {
      await AiKnowledgeSourcesAPI.create(
        { name: name.value.trim(), source_type: 'file' },
        selectedFile.value
      );
    } else {
      await AiKnowledgeSourcesAPI.create({
        name: name.value.trim(),
        source_type: 'website_url',
        source_url: sourceUrl.value.trim(),
      });
    }

    useAlert(t('AI_AGENTS.KNOWLEDGE.CREATE_SUCCESS'));
    emit('created');
    close();
  } catch {
    useAlert(t('AI_AGENTS.KNOWLEDGE.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

watch(
  () => props.open,
  isOpen => {
    if (!isOpen) resetForm();
  }
);
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="fixed inset-0 z-[70]">
      <div
        aria-hidden="true"
        class="absolute inset-0 bg-[rgba(24,24,27,0.65)]"
        @click="close"
      />
      <section
        class="absolute top-0 flex h-full w-full max-w-[452px] flex-col overflow-hidden rounded-l-2xl border-l border-n-weak bg-n-background shadow-2xl ltr:right-0 rtl:left-0"
        role="dialog"
        aria-modal="true"
        :aria-label="t('AI_AGENTS.KNOWLEDGE.MODAL.TITLE')"
        @click.stop
      >
        <header
          class="flex shrink-0 items-start justify-between gap-3 border-b border-n-weak px-5 py-4"
        >
          <div class="grid min-w-0 gap-1">
            <h2 class="m-0 text-base font-semibold text-n-slate-12">
              {{ t('AI_AGENTS.KNOWLEDGE.MODAL.TITLE') }}
            </h2>
            <p class="m-0 text-sm text-n-slate-11">
              {{ t('AI_AGENTS.KNOWLEDGE.MODAL.DESCRIPTION') }}
            </p>
          </div>
          <button
            type="button"
            class="flex size-8 shrink-0 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            @click="close"
          >
            <Icon icon="i-lucide-x" class="size-5" />
          </button>
        </header>

        <div
          class="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-5"
        >
          <Input
            v-model="name"
            :label="t('AI_AGENTS.KNOWLEDGE.SOURCE_NAME')"
            :placeholder="t('AI_AGENTS.KNOWLEDGE.MODAL.NAME_PLACEHOLDER')"
          />

          <div class="grid gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('AI_AGENTS.KNOWLEDGE.SOURCE_TYPE') }}
            </span>
            <div class="grid grid-cols-2 gap-3">
              <button
                type="button"
                class="flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors"
                :class="
                  sourceType === 'file'
                    ? 'border-n-brand bg-n-brand/5 text-n-slate-12'
                    : 'border-n-weak text-n-slate-11 hover:border-n-strong'
                "
                @click="sourceType = 'file'"
              >
                <span
                  class="size-4 shrink-0 rounded-full border-2"
                  :class="
                    sourceType === 'file'
                      ? 'border-n-brand bg-n-brand'
                      : 'border-n-strong'
                  "
                />
                <span class="min-w-0 flex-1 font-medium">
                  {{ t('AI_AGENTS.KNOWLEDGE.MODAL.TYPE_UPLOAD_FILE') }}
                </span>
                <Icon
                  icon="i-lucide-info"
                  class="size-4 shrink-0 text-n-slate-10"
                />
              </button>
              <button
                type="button"
                class="flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors"
                :class="
                  sourceType === 'website_url'
                    ? 'border-n-brand bg-n-brand/5 text-n-slate-12'
                    : 'border-n-weak text-n-slate-11 hover:border-n-strong'
                "
                @click="sourceType = 'website_url'"
              >
                <span
                  class="size-4 shrink-0 rounded-full border-2"
                  :class="
                    sourceType === 'website_url'
                      ? 'border-n-brand bg-n-brand'
                      : 'border-n-strong'
                  "
                />
                <span class="min-w-0 flex-1 font-medium">
                  {{ t('AI_AGENTS.KNOWLEDGE.MODAL.TYPE_ADD_WEBSITE') }}
                </span>
                <Icon
                  icon="i-lucide-info"
                  class="size-4 shrink-0 text-n-slate-10"
                />
              </button>
            </div>
          </div>

          <div v-if="sourceType === 'file'" class="grid gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('AI_AGENTS.KNOWLEDGE.MODAL.UPLOAD_FILE_LABEL') }}
            </span>
            <div
              class="grid min-h-[120px] place-content-center gap-1 rounded-lg border border-dashed px-4 py-8 text-center transition-colors"
              :class="
                isDragOver
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak bg-n-alpha-1'
              "
              @dragover.prevent="isDragOver = true"
              @dragleave.prevent="isDragOver = false"
              @drop.prevent="onDrop"
            >
              <p class="m-0 text-sm text-n-slate-11">
                {{ t('AI_AGENTS.KNOWLEDGE.MODAL.DROP_PREFIX') }}
                <button
                  type="button"
                  class="border-0 bg-transparent p-0 text-sm font-medium text-n-brand hover:underline"
                  @click="pickFile"
                >
                  {{ t('AI_AGENTS.KNOWLEDGE.MODAL.DROP_UPLOAD_LINK') }}
                </button>
                {{ t('AI_AGENTS.KNOWLEDGE.MODAL.DROP_SUFFIX') }}
              </p>
              <p
                v-if="selectedFile"
                class="m-0 truncate text-xs font-medium text-n-slate-12"
              >
                {{ selectedFile.name }}
              </p>
            </div>
            <p class="m-0 text-xs text-n-slate-11">
              {{ t('AI_AGENTS.KNOWLEDGE.MODAL.MAX_FILES_HINT') }}
            </p>
            <input
              ref="fileInputRef"
              type="file"
              class="hidden"
              accept=".pdf,.txt,.md,.csv,.json,text/plain,application/pdf"
              @change="onFileInputChange"
            />
          </div>

          <Input
            v-else
            v-model="sourceUrl"
            :label="t('AI_AGENTS.KNOWLEDGE.SOURCE_URL')"
            :placeholder="t('AI_AGENTS.KNOWLEDGE.MODAL.URL_PLACEHOLDER')"
            type="url"
          />
        </div>

        <footer
          class="flex shrink-0 items-center justify-between gap-3 border-t border-n-weak px-5 py-4"
        >
          <div
            v-if="sourceType === 'file'"
            class="flex items-center gap-1 text-xs text-n-slate-11"
          >
            <span>{{ selectedFileSizeLabel }}</span>
            <Icon icon="i-lucide-info" class="size-3.5 text-n-slate-10" />
          </div>
          <span v-else />

          <div class="flex items-center gap-2">
            <Button
              :label="t('AI_AGENTS.KNOWLEDGE.MODAL.CANCEL')"
              link
              slate
              @click="close"
            />
            <Button
              :label="t('AI_AGENTS.KNOWLEDGE.MODAL.DONE')"
              :disabled="!canSubmit"
              :is-loading="isSaving"
              @click="submit"
            />
          </div>
        </footer>
      </section>
    </div>
  </Teleport>
</template>
